<template>
  <prograd-loader
    v-if="showContentLoader"
    :flow="FlowConfigIdType.Prograd"
    :connect-accounts-clicked="handleConnectAccountsClick"
  />
  <div
    v-else
    class="page"
  >
    <riseup-header
      :flow-config-id="FlowConfigIdType.Prograd"
      :force-background-color="creds.length > 0 || isPolling ? '#E1F1F2' : '#FAECEA'"
    />
    <div
      v-if="showLoader"
      class="loader-wrapper"
    >
      <tiny-loader
        class="loader-class"
        variant="discount"
      />
    </div>
    <div
      v-else-if="creds.length > 0 || isPolling"
      class="content-wrapper"
    >
      <div class="content ready">
        <img
          src="../../../assets/prograd-ready-to-go.svg"
          class="connect-ready"
        >
        <span class="text center">
          Your ProGrad account is now being loaded with personalised recommendations for tasks to get you started.
        </span>
        <div class="center">
          <button
            class="green-button"
            @click="handleGoToInsightsClick"
          >
            Get personalised recommendations
          </button>
          <button
            class="outline-green-button"
            @click="handleConnectAccountsClick"
          >
            Connect another account
          </button>
          <ComplianceText />
        </div>
      </div>
    </div>
    <div
      v-else
      class="else-content"
    >
      <div class="content">
        <div class="center">
          <img
            src="../../../assets/prograd-connect-title.png"
            class="prograd-connect-title"
          >
          <div
            class="bullets-container"
          >
            <div class="bullet">
              <img
                src="../../../assets/white_check.svg"
                alt="Checkmark Icon"
              >
              <p class="text">
                Maximise your cash earnings potential by connecting the account you use most for spending.
              </p>
            </div>
            <div class="bullet">
              <img
                src="../../../assets/white_check.svg"
                alt="Checkmark Icon"
              >
              <p class="text">
                Get personalised recommendations in minutes.
              </p>
            </div>
          </div>
          <div class="flex-container">
            <div
              class="etoro-flex-box"
            >
              <div class="icon">
                <img
                  src="../../../assets/prograd-lock.svg"
                  alt="Shield Icon"
                >
              </div>
              <div class="text">
                <span>ProGrad does not keep your data. We connect once to create your personalised recommendations.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="footer">
        <img
          src="../../../assets/connect-certs.png"
          class="connect-certs"
        >
        <button
          class="green-button"
          @click="handleConnectAccountsClick"
        >
          Connect accounts
        </button>
        <ComplianceText />
      </div>
    </div>
  </div>
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import { onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getCustomerCredentials } from '../../../api/cashflowApi';
import { authenticateTrueLayerCode, getTrueLayerUrl } from '../../../api/trueLayer';
import useContentLoader from '../../../composables/useContentLoader';
import config from '../../../config';
import Segment from '../../../Segment';
import { useInsights } from '../../../services/insightsService';
import { useOtsJob } from '../../../services/otsConfigService';
import { useSessionStore } from '../../../stores/session';
import { FlowConfigIdType } from '../../../types/otsConfig';
import { _descopeAutheticate } from '../../../utils/descopeUtils';
import RiseupHeader from '../../Common/RiseupHeader.vue';
import ProgradLoader from './ProgradLoader.vue';
import ComplianceText from '../../Common/ComplianceText.vue';

const sessionStore = useSessionStore();
const { isPolling, updateIsPolling, startPollingByJobId } = useInsights();
const router = useRouter();
const route = useRoute();
const { toggleLoader, showContentLoader } = useContentLoader();
Segment.identifyCustomer(sessionStore.customerId, sessionStore.memberId);
const { getOtsJobConfig } = useOtsJob();

const otsJobConfig = await getOtsJobConfig();
const creds = ref([]);
const showLoader = ref(false);

const handleConnectAccountsClick = async () => {
  Segment.trackUserGot('ProgradConnectOtsAccountsConnectClicked', { gclid: Cookies.get('gclid') });
  window.location.href = await getTrueLayerUrl({ jobId: otsJobConfig.jobId }, config.get().otsProgradEnv);
};

const handleGoToInsightsClick = async () => {
  Segment.trackUserGot('ProgradConnectOtsAccountsGetInsightsClicked', { gclid: Cookies.get('gclid') });

  toggleLoader(true);
  startPollingByJobId(otsJobConfig.jobId, 300, 3000, () => { router.push('/ots/prograd/success'); }, handlePollingError);
};

const handlePollingError = () => {
  router.push('/ots/prograd/connect');
  throw new Error(`Could not get insights in time, ${sessionStore.customerId}`);
};

onBeforeMount(async () => {
  toggleLoader(false);
  showLoader.value = true;
  const queryParam = route.query || null;
  if (queryParam.token) {
    const isDescopeAutheticated = await _descopeAutheticate(queryParam.token);
    if (isDescopeAutheticated) {
      const sessionStore = useSessionStore();
      await sessionStore.init();
    }
  }

  creds.value = await getCustomerCredentials();
  if (queryParam.code) {
    updateIsPolling(true);
    const response = await authenticateTrueLayerCode(queryParam.code);
    if (response) {
      startPollingByJobId(otsJobConfig.jobId, 300, 3000, () => { router.push('/ots/prograd/success'); }, handlePollingError);
    } else {
      updateIsPolling(false);
    }
  }
  showLoader.value = false;

  Segment.trackUserGot('ProgradConnectOtsAccountPageEntered');
});

</script>

<style lang="scss" scoped>
@import '../../../scss/riseup-vars';

.page {
  background-color: #FAECEA;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.content {
  display: flex;
  flex-direction: column;
  margin: 0 auto;
}

.content .text {
  color: #202125;
  font-size: 18px;
  font-family: CircularStdBook;
  font-weight: 450;
  line-height: 28px;
  margin-bottom: 20px;
}

.flex-container {
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  margin-top: 40px;
}

.etoro-flex-box {
  padding: 15px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  flex-direction: row;
  width: 100%;
}

.etoro-flex-box .text {
  margin: 0 auto;
  text-align: left;
  font-size: 15px;
  line-height: normal;
}

.etoro-flex-box img {
  width: 30px;
  height: 30px;
}

.icon {
  margin-right: 20px;
  display: flex;
  align-items: center;
}

.footer {
  width: 100%;
  text-align: center;
  position: relative;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #FAECEA;
  padding-bottom: 20px;
}

.green-button {
  background-color: #269B74;
  border-radius: 65px;
  color: white;
  padding: 19px 20px;
  border: none;
  margin: 10px 0;
  width: 90%;
  cursor: pointer;
  font-size: 16px;
  font-family: CircularStdBook;
  font-weight: 700;
  align-self: center;
}

.outline-green-button {
  color: #269B74;
  background-color: transparent;
  border-radius: 65px;
  padding: 19px 20px;
  border: 1px solid #269B74;
  margin: 10px 0;
  width: 90%;
  cursor: pointer;
  font-size: 16px;
  font-family: CircularStdBook;
  font-weight: 700;
}

.bullets-container {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.bullet {
  display: flex;
  align-items: center;
  gap: 15px;
}

.bullet img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.bullet .text {
  text-align: left;
  font-size: 15px;
  line-height: normal;
  display: flex;
  align-items: flex-start;
  margin: 0;
}

.prograd-connect-title {
  width: 100%;
  padding: 0px 30px;
  margin-top: 40px;
  margin-bottom: 20px;
}

.connect-ready {
  width: 100%;
  padding: 25px 25px 0px 25px;
  margin-top: 70px;
  margin-bottom: 40px;
}

.ready {
  background-color: #E1F1F2;
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: -25px;
  padding: 0px 60px;
  overflow-y: auto;
}

.center {
  text-align: center;
}

.else-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 90%;
  margin: 0 auto;
  flex: 1;
  justify-content: space-between;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.connect-certs {
  width: 95%;
  padding: 0px 30px;
  margin-top: 40px;
  margin-bottom: 10px;
}
</style>
